import { Assets, type Texture } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import type { StoryArtName } from './StoryLayer';

/** Where each piece of story art comes from: a file of its own (`url`),
 *  or a texture the slot already loaded (`alias`). */
export type StoryArtSources = Partial<Record<StoryArtName, { url?: string | null; alias?: string }>>;

/**
 * Load the story's own art (only for players with the story, so nobody else
 * fetches it) and return a lookup. Art that fails to load reads as null and
 * the screens fall back to drawing without it.
 */
export async function loadStoryArt(sources: StoryArtSources): Promise<(name: StoryArtName) => Texture | null> {
  const loaded = new Map<StoryArtName, Texture>();
  await Promise.all(
    (Object.entries(sources) as [StoryArtName, { url?: string | null; alias?: string }][]).map(async ([name, src]) => {
      if (!src.url) return;
      try {
        loaded.set(name, await Assets.load<Texture>(src.url));
      } catch (err) {
        console.warn('[story] art did not load', name, err);
      }
    }),
  );
  return (name) => {
    const own = loaded.get(name);
    if (own) return own;
    const alias = sources[name]?.alias;
    return alias ? AssetLoader.shared.getTexture(alias) : null;
  };
}
